<template>
  <div class="document-type-grid">
    <div class="grid-header">
      <h6 class="grid-title">สร้างเอกสาร</h6>
    </div>

    <!-- Compact single-row layout -->
    <div class="document-actions">
      <!-- Medical Certificates -->
      <div class="action-group" v-if="medicalCertificates.length > 0">
        <span class="group-label">ใบรับรอง</span>
        <div class="action-chips">
          <DocumentTypeCard
            v-for="item in medicalCertificates" 
            :key="item.value"
            :title="item.text"
            :value="item.value"
            :disabled="item.disabled"
            :is-generating="generatingDocument === item.value"
            @generate="handleGenerate"
          />
        </div>
      </div>

      <!-- Consent Forms -->
      <div class="action-group" v-if="consentForms.length > 0">
        <span class="group-label">ใบยินยอม</span>
        <div class="action-chips">
          <DocumentTypeCard
            v-for="item in consentForms" 
            :key="item.value"
            :title="item.text"
            :value="item.value"
            :disabled="item.disabled"
            :is-generating="generatingDocument === item.value"
            @generate="handleGenerate"
          />
        </div>
      </div>

      <!-- Visual Separator -->
      <div v-if="templateDocuments.length > 0 || showTemplateUpload" class="section-divider"></div>

      <!-- Branch Template Documents -->
      <div class="action-group template-section" v-if="templateDocuments.length > 0 || showTemplateUpload">
        <span class="group-label">แบบฟอร์มคลินิก</span>
        <div class="action-chips">
          <!-- Existing Template Documents -->
          <DocumentTypeCard
            v-for="template in templateDocuments"
            :key="template.id"
            :title="template.name || template.remark"
            :value="template.id"
            :disabled="false"
            :is-generating="signingTemplate === template.id"
            :deletable="canManageClinicForm"
            @generate="handleTemplateSign"
            @delete="handleTemplateDelete"
          />
          
          <!-- Upload New Template Button -->
          <DocumentTypeCard
            v-if="showTemplateUpload"
            title="อัปโหลด PDF แบบฟอร์ม"
            value="upload_template"
            :disabled="false"
            :is-generating="false"
            :is-upload="true"
            @generate="handleTemplateUpload"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DocumentTypeCard from './DocumentTypeCard.vue';
import ClinicFormPermissionMixin from "@/mixins/ClinicFormPermissionMixin";

export default {
  name: "DocumentTypeGrid",
  mixins: [ClinicFormPermissionMixin],
  components: {
    DocumentTypeCard,
  },
  props: {
    documentTypes: {
      type: Array,
      default: () => [],
    },
    generatingDocument: {
      type: String,
      default: null,
    },
    templateDocuments: {
      type: Array,
      default: () => [],
    },
    // mediaFile id ของแบบฟอร์มเป็น Number ส่วน key ของเอกสารที่ generate เป็น String
    signingTemplate: {
      type: [String, Number],
      default: null,
    },
  },
  computed: {
    medicalCertificates() {
      return this.flattenedDocuments.filter(item => 
        ['medicalCert', 'referralLetter'].includes(item.value) && !item.disabled
      );
    },
    consentForms() {
      return this.flattenedDocuments.filter(item => 
        item.value.includes('Consent') && !item.disabled
      );
    },
    flattenedDocuments() {
      const flattened = [];
      this.documentTypes.forEach(item => {
        if (item.options) {
          // Handle grouped options (consent forms)
          item.options.forEach(option => {
            flattened.push(option);
          });
        } else if (item.value !== 'empty') {
          // Handle regular items (exclude the disabled placeholder)
          flattened.push(item);
        }
      });
      return flattened;
    },
    showTemplateUpload() {
      // เพิ่มแบบฟอร์มได้เฉพาะผู้จัดการสาขา / RDM / C_LEVEL
      return this.canManageClinicForm;
    },
  },
  methods: {
    handleGenerate(documentType) {
      this.$emit('generate', documentType);
    },
    handleTemplateSign(templateId) {
      this.$emit('template-sign', templateId);
    },
    handleTemplateUpload() {
      this.$emit('template-upload');
    },
    handleTemplateDelete(templateId) {
      this.$emit('template-delete', templateId);
    },
    getDocumentDescription(type) {
      const descriptions = {
        medicalCert: 'ใบรับรองทันตแพทย์สำหรับการลา หรือการแสดงหลักฐาน',
        referralLetter: 'หนังสือส่งตัวผู้ป่วยไปยังแพทย์เฉพาะทาง',
        dentalImplantConsent: 'ใบยินยอมสำหรับการรักษาด้วยรากเทียม',
        veneerConsent: 'ใบยินยอมสำหรับการติดวีเนียร์',
        traditionalBracesConsent: 'ใบยินยอมสำหรับการจัดฟันแบบดั้งเดิม',
        clearAlignerConsent: 'ใบยินยอมสำหรับการจัดฟันใส',
        marketingConsent: 'ใบยินยอมการใช้ข้อมูลเพื่อการตลาด',
      };
      return descriptions[type] || '';
    },
    getDocumentIcon(type) {
      const icons = {
        medicalCert: 'fas fa-file-medical',
        referralLetter: 'fas fa-file-export',
        dentalImplantConsent: 'fas fa-tooth',
        veneerConsent: 'fas fa-smile',
        traditionalBracesConsent: 'fas fa-teeth',
        clearAlignerConsent: 'fas fa-teeth-open',
        marketingConsent: 'fas fa-bullhorn',
      };
      return icons[type] || 'fas fa-file-alt';
    },
  },
  emits: ['generate', 'template-sign', 'template-upload', 'template-delete'],
};
</script>

<style scoped>
.document-type-grid {
  padding: 0;
}

.grid-header {
  text-align: center;
  margin-bottom: 1rem;
}

.grid-title {
  color: #2c3e50;
  font-weight: 600;
  margin-bottom: 0;
  font-size: 1rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  display: inline-block;
}

.document-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.action-group {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 0.25rem;
}

.group-label {
  color: #6c757d;
  font-weight: 500;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  min-width: 100px;
  text-align: right;
  flex-shrink: 0;
}

.action-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  flex: 1;
}

.section-divider {
  width: 100%;
  height: 1px;
  background: linear-gradient(to right, transparent 0%, #dee2e6 20%, #dee2e6 80%, transparent 100%);
  margin: 0.75rem 0;
  position: relative;
}

.section-divider::before {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 50%;
}

.template-section {
  margin-top: 0.5rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .grid-title {
    font-size: 0.9rem;
  }
  
  .document-actions {
    gap: 0.75rem;
  }
  
  .action-group {
    gap: 0.5rem;
  }
  
  .group-label {
    font-size: 0.7rem;
    min-width: 80px;
  }
  
  .action-chips {
    gap: 0.375rem;
  }
}

@media (max-width: 576px) {
  .grid-title {
    font-size: 0.85rem;
  }
  
  .action-group {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.375rem;
  }
  
  .group-label {
    text-align: left;
    min-width: auto;
    font-size: 0.65rem;
  }
  
  .action-chips {
    gap: 0.25rem;
  }
}
</style>